import { useEffect, useRef, useState } from "react"

import { Button } from "@coval/ui"
import { Download, Loader2 } from "lucide-react"

import { downloadBlob } from "@/utils/download"

import { captureNotebookSlides } from "../utils/captureNotebookSlides"
import { notebookPdfFilename } from "../utils/notebookExport"
import type { notebookSlides } from "../utils/notebookSlides"
import type { Notebook } from "../utils/notebookSnapshot"
import { NotebookCanvas } from "./NotebookCanvas"
import styles from "./NotebookPresentation.module.css"
import { NotebookPrintButton } from "./NotebookPrintButton"

export function NotebookPresentationExport({
  notebook,
  slides,
  organizationSlug,
  workspaceId,
  disabled,
}: {
  notebook: Notebook
  slides: ReturnType<typeof notebookSlides>
  organizationSlug: string
  workspaceId: string
  disabled: boolean
}) {
  const root = useRef<HTMLDivElement>(null)
  const pending = useRef<AbortController | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")
  const [warning, setWarning] = useState("")
  useEffect(() => {
    pending.current = null
    setBusy(false)
    setError("")
    setWarning("")
    return () => pending.current?.abort()
  }, [notebook, organizationSlug, workspaceId, disabled])
  const handleExport = async () => {
    if (disabled || pending.current || !slides.length) {
      return
    }
    const controller = new AbortController()
    pending.current = controller
    setBusy(true)
    setError("")
    setWarning("")
    try {
      const [pages, { buildNotebookSlidesPdf }] = await Promise.all([
        captureNotebookSlides(root, slides.length, controller.signal),
        import("./NotebookSlidesPdf"),
      ])
      controller.signal.throwIfAborted()
      const blob = await buildNotebookSlidesPdf(notebook.title, pages)
      if (!controller.signal.aborted) {
        downloadBlob(blob, notebookPdfFilename(notebook.title))
        const omitted = pages.flatMap((page) => page.warnings).length
        if (omitted) {
          setWarning(
            `PDF downloaded. ${omitted} ${omitted === 1 ? "image could" : "images could"} not be embedded; labelled placeholders and available source links are included.`,
          )
        }
      }
    } catch (failure) {
      if (!controller.signal.aborted) {
        setError(
          failure instanceof Error
            ? failure.message
            : "Could not export the slides. Try again.",
        )
      }
    } finally {
      if (!controller.signal.aborted) {
        pending.current = null
        setBusy(false)
      }
    }
  }
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-start gap-1">
        <NotebookPrintButton
          id={notebook.id}
          title={notebook.document?.title ?? notebook.title}
          disabled={disabled}
        />
        <Button
          variant="secondary"
          disabled={disabled || busy || !slides.length}
          onClick={() => void handleExport()}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Download />}
          {busy ? "Preparing PDF…" : "Export PDF"}
        </Button>
      </div>
      <p className="max-w-sm text-xs text-coval-text-muted">
        PDFs show still images and video source links. Playback stays in the
        notebook.
      </p>
      {error && (
        <p role="alert" className="max-w-sm text-sm text-coval-danger-text">
          {error}
        </p>
      )}
      {warning && (
        <p role="status" className="max-w-sm text-sm text-coval-text-muted">
          {warning}
        </p>
      )}
      {busy && (
        <div
          ref={root}
          aria-hidden
          className="pointer-events-none fixed -left-[10000px] top-0 w-[1200px]"
        >
          {slides.map((slide, index) => (
            <div key={index} data-pdf-slide className={styles.slide}>
              <NotebookCanvas
                notebook={notebook}
                content={slide.content}
                organizationSlug={organizationSlug}
                workspaceId={workspaceId}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
